var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import flatten from 'lodash/flatten';
import uniqWith from 'lodash/uniqWith';
import { useEffect, useMemo, useState } from 'react';
import { prepareInlineImages } from 'app/utils/attachments';
import { createAttachedFiles } from './AttachedFile';
import { InlinedImages } from './inlinedImages/InlinedImages';
/**
 * Represents the image size, either width or height, that determines the
 * threshold of whether the image is considered tiny.
 */
const tinyImageSizeThreshold = 24;
/** Creates image and attachments thumbnail as react nodes */
export function useMessageFiles(imagesInput, attachmentsInput) {
    const [renderedImageNodes, setRenderedImageNodes] = useState([]);
    const [unrenderedImageFiles, setUnrenderedImageFiles] = useState([]);
    const images = useMemo(() => flatten([imagesInput !== null && imagesInput !== void 0 ? imagesInput : []]), [imagesInput]);
    const attachments = useMemo(() => flatten([attachmentsInput !== null && attachmentsInput !== void 0 ? attachmentsInput : []]), [attachmentsInput]);
    // Handles fetching the sizes of the images and creating their compositions
    useEffect(() => {
        const prepareImages = () => __awaiter(this, void 0, void 0, function* () {
            const renderedImages = [];
            const unrenderedImages = [];
            const uniqueFiles = uniqWith(prepareInlineImages(images), (a, b) => a.absolute_uri === b.absolute_uri);
            const results = yield Promise.allSettled(uniqueFiles.map((file) => __awaiter(this, void 0, void 0, function* () {
                try {
                    return {
                        file,
                        image: yield getImage(file.absolute_uri),
                    };
                }
                catch (_a) {
                    return {
                        file,
                        image: null,
                    };
                }
            })));
            uniqWith(results, (a, b) => a.status === 'fulfilled' &&
                b.status === 'fulfilled' &&
                !!a.value.image &&
                !!b.value.image &&
                a.value.image.readerResult === b.value.image.readerResult).forEach((result) => {
                if (result.status !== 'fulfilled') {
                    return;
                }
                const { file, image } = result.value;
                if (!image) {
                    unrenderedImages.push({
                        url: file.absolute_uri,
                        pretty_name: file.name,
                    });
                    return;
                }
                // Filter out repetitive or tiny images
                if (image.imageElement.width <= tinyImageSizeThreshold ||
                    image.imageElement.height <= tinyImageSizeThreshold) {
                    return;
                }
                renderedImages.push({ url: file.absolute_uri, pretty_name: file.name });
            });
            setRenderedImageNodes(renderedImages);
            setUnrenderedImageFiles(unrenderedImages);
        });
        prepareImages();
    }, [images]);
    return useMemo(() => ({
        images: _jsx(InlinedImages, { files: renderedImageNodes }),
        attachments: createAttachedFiles([
            ...unrenderedImageFiles,
            ...attachments,
        ]),
    }), [attachments, renderedImageNodes, unrenderedImageFiles]);
}
function getImage(url) {
    const image = new Image();
    return new Promise((resolve, reject) => {
        image.src = url;
        image.onload = () => {
            fetch(url).then((response) => __awaiter(this, void 0, void 0, function* () {
                const blob = yield response.blob();
                const reader = new FileReader();
                reader.onloadend = () => {
                    resolve({
                        imageElement: image,
                        blob,
                        readerResult: reader.result,
                    });
                };
                reader.readAsDataURL(blob);
            }));
            image.remove();
        };
        image.onerror = () => {
            reject();
            image.remove();
        };
    });
}
